import type { BoxProps } from '@hover/blueprint';
import { Body, Box, Center, Image, motion } from '@hover/blueprint';
import type { TFunction } from 'i18next';
import type { AiDisplayImage } from 'mock/generated/types';
import React, { memo } from 'react';

import { useHomeownerTranslate } from 'src/modules/homeownerApp/hooks';

import { animationVariants } from './Images';

const MotionBox = motion(Box);

export interface ImagesProps extends BoxProps {
  aiDisplayImages: AiDisplayImage[];
  visibleIndex: number;
}

export interface ImageProps extends ImagesProps {
  imageIndex: number;
}

export const getAiGeneratedImageStyle = (t: TFunction, style: string = '') =>
  style === t('share.myUpload') ? t('share.edited') : style;

const OriginalImage = memo(
  ({ aiDisplayImages, imageIndex, visibleIndex, ...props }: ImageProps) => {
    const image = aiDisplayImages[imageIndex];
    const show = imageIndex >= visibleIndex;

    return (
      <MotionBox
        alignItems="center"
        animate={show ? 'show' : 'hide'}
        color="neutral.500"
        display="flex"
        justifyContent="center"
        position={
          aiDisplayImages.length - 1 > imageIndex ? 'absolute' : 'inherit'
        }
        top={0}
        variants={animationVariants}
        zIndex={aiDisplayImages.length - imageIndex}
      >
        <Center
          borderRadius={400}
          maxWidth={{ tablet: '460px' }}
          overflow="hidden"
          sx={{ aspectRatio: '460 / 600' }}
          width="100%"
          {...props}
        >
          <Image
            alt="original image"
            height="100%"
            objectFit="cover"
            src={image.aiOriginalImage?.originalImagePath}
            width="100%"
          />
        </Center>
      </MotionBox>
    );
  },
  (prevProps: ImageProps, nextProps: ImageProps) => {
    const prevPath =
      prevProps.aiDisplayImages[prevProps.visibleIndex].aiOriginalImage
        ?.originalImagePath;
    const nextPath =
      nextProps.aiDisplayImages[nextProps.visibleIndex].aiOriginalImage
        ?.originalImagePath;

    return prevPath === nextPath;
  },
);

export const OriginalImages = ({
  aiDisplayImages,
  visibleIndex,
  ...props
}: ImagesProps) => {
  const t = useHomeownerTranslate();

  if (!aiDisplayImages) return null;

  return (
    <Box flexDirection="column" {...props}>
      <Box flexDirection="column" position="relative">
        <Box
          borderRadius={400}
          display="inline-block"
          flexDirection="row"
          overflow="hidden"
          position="relative"
          zIndex={0}
        >
          {aiDisplayImages.map((image, index) => (
            <OriginalImage
              aiDisplayImages={aiDisplayImages}
              imageIndex={index}
              key={image.id}
              visibleIndex={visibleIndex}
              {...props}
            />
          ))}
        </Box>
      </Box>
      <Body fontWeight={700} marginBottom={0} marginTop="20px">
        {t('share.original')}
      </Body>
    </Box>
  );
};
